/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Payment Summary Table Component
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import { Button as AntButton, App, Card, Collapse, Result, TableColumnsType } from 'antd';
import Table from 'lib/antd/Table';
import { DeleteOne } from '@icon-park/react';
import NO_DATA_FOUND from 'assets/custom-no-data.svg';

import { formatAmount } from 'utils/accounts/allAccounts';
import { getPaymentSummaryTableData } from 'utils/payment/payments';
import { getClaimResponsePath, getPatientDetailsPath } from 'utils/router';
import useUpdatePaymentReconciliation from 'hooks/payments/mutate/useUpdatePaymentReconciliation';
import { MODAL_TYPES } from 'components/widgets/modal';
import { EMPTY_VALUE } from '@veritable/common/constants';
import { toCents } from 'utils/claims/postPayment';

import AccountCollapseHeader from 'components/payments/paymentAllocation/AccountCollapseHeader';

type SummaryAccountGroup = {
	key: string;
	accountIdentifier: PatientPayment.ISummaryTableData['accountIdentifier'];
	outstandingPR: number;
	rows: PatientPayment.ISummaryTableData[];
};

const getDeallocationMessage = ({ refundAmount, writeOffAmount }: { refundAmount: number; writeOffAmount: number }) => {
	const writeOffAmountString = formatAmount(writeOffAmount);
	const refundAmountString = formatAmount(refundAmount);
	// TODO: Check and fix the logic for refund amount should be writeOff > 0
	if (refundAmount > 0 && writeOffAmount > 0) {
		return (
			<>
				{`Deallocating payment from this claim will also remove the associated write-off of ${writeOffAmountString} and refund of ${refundAmountString} recorded for this payment.`}
				<br />
				{'Are you sure you want to proceed?'}
			</>
		);
	}
	if (writeOffAmount > 0) {
		return (
			<>
				{`Deallocating payment from this claim will also remove the write-off of ${writeOffAmountString} recorded for this claim.`}
				<br />
				{'Do you want to continue?'}
			</>
		);
	}
	if (refundAmount > 0) {
		return (
			<>
				{`Deallocating payment from this claim will also remove the refund of ${refundAmountString} recorded for this payment.`}
				<br />
				{'Are you sure you want to proceed?'}
			</>
		);
	}
	return 'Are you sure you want to deallocate payment for this claim?';
};

const getSummaryAccountGroupKey = (row: PatientPayment.ISummaryTableData) => {
	return row?.accountIdentifier?.id || row?.accountIdentifier?.accountNumber || 'no-account';
};

const PaymentSummaryTable: React.FC<{
	data: PatientPayment.IPaymentReconciliation;
	parsedAllocationData: PatientPayment.IParsedPaymentReconciliation;
	id: string;
	allowDelete?: boolean;
	showTitle?: boolean;
	titleText?: string;
	extra?: React.ReactNode;
}> = ({ data, parsedAllocationData, id, allowDelete = true, showTitle = true, titleText = 'Summary', extra }) => {
	const parsedTableData = useMemo(
		() =>
			getPaymentSummaryTableData({
				allAllocations: data?.allocations,
				parsedAllocations: parsedAllocationData.allocations,
			}),
		[data?.allocations, parsedAllocationData.allocations],
	);

	const accountGroups = useMemo<SummaryAccountGroup[]>(() => {
		const groupedRows = _.groupBy(parsedTableData, getSummaryAccountGroupKey);
		return _.map(groupedRows, (rows, key) => ({
			key,
			accountIdentifier: rows?.[0]?.accountIdentifier,
			outstandingPR: _.sumBy(rows, (row) => row.pendingPRAmount || 0),
			rows,
		}));
	}, [parsedTableData]);

	const { mutateAsync } = useUpdatePaymentReconciliation(id);
	const { modal } = App.useApp();
	const handleDelete = ({ claimIdentifier, writeOffAmount }: PatientPayment.ISummaryTableData) => {
		const claimId = claimIdentifier?.id;
		const refundAmount = parsedAllocationData?.refundAmount || 0;

		const submitModel = {
			allocations: _.filter(data?.allocations, ({ fhirClaim }) => fhirClaim?.id !== claimId),
			fhirResource: {
				...data?.fhirResource,
				allocation: _.filter(data?.fhirResource?.allocation, (allocation) => allocation?.target?.reference !== `Claim/${claimId}`),
				outcome: 'partial',
				returnedAmount: {
					value: 0,
					currency: 'USD',
				},
			},
		};
		modal.confirm({
			...MODAL_TYPES.confirm,
			title: 'Delete Allocation',
			content: getDeallocationMessage({ refundAmount, writeOffAmount }),
			onOk: async () => {
				await mutateAsync({ modal: submitModel });
			},
			okText: 'Yes, Deallocate',
		});
	};

	const columns: TableColumnsType<PatientPayment.ISummaryTableData> = [
		{
			title: 'Patient Name',
			dataIndex: 'patientIdentifier',
			key: 'patientIdentifier',
			minWidth: 150,
			render: (patientIdentifier: { name: string; id: string }) => {
				const patientId = patientIdentifier?.id;
				const patientName = patientIdentifier?.name || <i>{'No Patient Name'}</i>;
				if (patientId) {
					return (
						<Link target='_blank' to={getPatientDetailsPath({ id: patientId })}>
							{patientName}
						</Link>
					);
				}
				return patientName;
			},
		},
		{
			title: 'Claim ID',
			dataIndex: 'claimIdentifier',
			key: 'claimId',
			minWidth: 150,
			render: (claimIdentifier: PatientPayment.ISummaryTableData['claimIdentifier']) => {
				const claimId = claimIdentifier?.id;
				const claimNumber = claimIdentifier?.name || <i>{'No Claim ID'}</i>;
				if (claimId) {
					return (
						<Link target='_blank' to={getClaimResponsePath(claimId)}>
							{claimNumber}
						</Link>
					);
				}
				return claimNumber;
			},
		},
		{
			title: 'Allocated',
			dataIndex: 'allocatedAmount',
			key: 'allocatedAmount',
			minWidth: 150,
			align: 'right',
			render: (allocatedAmount: PatientPayment.ISummaryTableData['allocatedAmount']) => formatAmount(allocatedAmount || 0),
		},
		{
			title: 'Pending PR',
			dataIndex: 'pendingPR',
			key: 'pendingPR',
			minWidth: 150,
			onCell: () => {
				return { style: { textAlign: 'right' } };
			},
			render: (pendingPR: PatientPayment.ISummaryTableData['pendingPR']) => pendingPR ?? EMPTY_VALUE,
		},
		{
			title: 'Write-Off',
			dataIndex: 'writeOffAmount',
			key: 'writeOff',
			minWidth: 150,
			onCell: () => {
				return { style: { textAlign: 'right' } };
			},
			render: (writeOffAmount: PatientPayment.ISummaryTableData['writeOffAmount']) => formatAmount(writeOffAmount) ?? EMPTY_VALUE,
		},
		{
			title: '',
			key: 'actions',
			minWidth: 50,
			hidden: !allowDelete,
			render: (record: PatientPayment.ISummaryTableData) => {
				return (
					<AntButton
						disabled={!allowDelete}
						danger
						size='small'
						icon={<DeleteOne fill='#FF4D4F' />}
						onClick={() => handleDelete(record)}
					/>
				);
			},
		},
	];

	const collapseItems = useMemo(
		() =>
			_.map(accountGroups, (group) => ({
				key: group.key,
				label: (
					<AccountCollapseHeader
						accountNumber={group.accountIdentifier?.accountNumber}
						accountAge={group.accountIdentifier?.accountAge}
						outstandingPR={group.outstandingPR}
						accountId={group.accountIdentifier?.id}
					/>
				),
				className: 'payment-allocation-account-collapse-panel',
				children: <Table columns={columns} dataSource={group.rows} bordered size='small' pagination={false} rowKey='key' />,
			})),
		[accountGroups, columns],
	);

	const defaultActiveKey = useMemo(() => _.map(accountGroups, (group) => group.key), [accountGroups]);
	const cardClassName = extra
		? 'payment-allocation-summary-card'
		: 'payment-allocation-summary-card payment-allocation-summary-card-centered-title';
	const hasNoAllocations = _.isEmpty(parsedAllocationData?.allocations);
	const hasRefund = toCents(parsedAllocationData?.refundAmount || 0) > 0;
	const isFullyRefundedWithoutAllocations =
		hasNoAllocations &&
		toCents(parsedAllocationData?.paymentAmount || 0) > 0 &&
		toCents(parsedAllocationData?.refundAmount || 0) >= toCents(parsedAllocationData?.paymentAmount || 0);
	const isUnallocatedWithoutRefund = hasNoAllocations && !hasRefund;

	const summaryContent = useMemo(() => {
		if (isFullyRefundedWithoutAllocations) {
			return (
				<Result
					className='payment-allocation-summary-empty-result'
					icon={<img src={NO_DATA_FOUND} alt='no-data' />}
					title='Payment fully refunded'
					subTitle='This payment has no allocations because the full payment amount was refunded.'
				/>
			);
		}

		if (isUnallocatedWithoutRefund) {
			return (
				<Result
					className='payment-allocation-summary-empty-result'
					icon={<img src={NO_DATA_FOUND} alt='no-data' />}
					title='No allocations recorded'
					subTitle='This payment has not been allocated or refunded yet. Allocate it to a patient claim or record a refund for the remaining balance.'
				/>
			);
		}

		return (
			<Collapse
				bordered={false}
				className='payment-allocation-account-collapse payment-allocation-summary-collapse'
				defaultActiveKey={defaultActiveKey}
				items={collapseItems}
			/>
		);
	}, [collapseItems, defaultActiveKey, isFullyRefundedWithoutAllocations, isUnallocatedWithoutRefund]);

	return (
		<Card className={cardClassName} variant='borderless' title={showTitle ? titleText : undefined} extra={extra}>
			{summaryContent}
		</Card>
	);
};

export default PaymentSummaryTable;
